import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TrendingUp, Percent, Weight, Activity } from "lucide-react";
import { api } from "@/lib/api";
import { fmt } from "@/lib/format";

function KPI({ label, value, sub, icon: Icon, testid }) {
  return (
    <div className="kpi-card" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</div>
          <div className="mt-2 font-heading text-2xl font-semibold tracking-tight">{value}</div>
          {sub && <div className="subtle mt-1">{sub}</div>}
        </div>
        {Icon && <div className="h-10 w-10 rounded-xl bg-deepgreen/10 text-deepgreen flex items-center justify-center"><Icon className="h-5 w-5" /></div>}
      </div>
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/analytics/poultry").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="p-8"><div className="subtle">Loading analytics…</div></div>;
  const cur = data.current;
  const prev = data.previous;
  const avg = data.historical_average || {};
  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Poultry360</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight">Performance Analytics</h1>
        <p className="subtle mt-1">FCR, ADG, mortality, profitability and batch scoring.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPI label="FCR" value={fmt(cur?.fcr, 2)} sub={`avg ${fmt(avg.fcr, 2)}`} icon={Percent} testid="an-fcr" />
        <KPI label="Mortality %" value={fmt(cur?.mortality_pct, 2)} sub={`avg ${fmt(avg.mortality_pct, 2)}`} icon={Activity} testid="an-mortality" />
        <KPI label="Livability %" value={fmt(cur?.livability_pct, 2)} sub={`avg ${fmt(avg.livability_pct, 2)}`} icon={TrendingUp} testid="an-livability" />
        <KPI label="ADG (g/day)" value={fmt(cur?.adg_g_per_day, 1)} sub={`avg ${fmt(avg.adg_g_per_day, 1)}`} icon={Weight} testid="an-adg" />
        <KPI label="Revenue" value={fmt(cur?.total_revenue)} sub={`per bird ${fmt(cur?.revenue_per_bird)}`} testid="an-revenue" />
        <KPI label="Profit" value={fmt(cur?.profit)} sub={`margin ${fmt(cur?.profit_margin_pct, 1)}%`} testid="an-profit" />
        <KPI label="Cost / kg" value={fmt(cur?.cost_per_kg)} sub={`per bird ${fmt(cur?.cost_per_bird)}`} testid="an-cost" />
        <KPI label="Batch score" value={fmt(cur?.performance_score, 0)} sub={`avg ${fmt(avg.performance_score, 0)}`} icon={TrendingUp} testid="an-score" />
      </div>

      {!cur ? (
        <Card className="rounded-2xl"><CardContent className="py-8 subtle text-center">No batches recorded yet — capture a placement in Poultry360 to populate performance analytics.</CardContent></Card>
      ) : (
        <>
          <Card className="rounded-2xl">
            <CardHeader><CardTitle>Current vs Previous vs Average</CardTitle><CardDescription>Batch performance comparison</CardDescription></CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Metric</TableHead><TableHead>Current</TableHead><TableHead>Previous</TableHead><TableHead>Historical Avg</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {[
                    ["FCR", "fcr"], ["Mortality %", "mortality_pct"], ["Livability %", "livability_pct"],
                    ["ADG (g/day)", "adg_g_per_day"], ["Profit margin %", "profit_margin_pct"], ["Score", "performance_score"],
                  ].map(([label, k]) => (
                    <TableRow key={k}>
                      <TableCell className="font-medium">{label}</TableCell>
                      <TableCell>{cur[k] ?? "—"}</TableCell>
                      <TableCell>{prev?.[k] ?? "—"}</TableCell>
                      <TableCell>{avg[k] ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card className="rounded-2xl">
            <CardHeader><CardTitle>All batches</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Batch</TableHead><TableHead>Age (days)</TableHead><TableHead>FCR</TableHead>
                  <TableHead>Mortality %</TableHead><TableHead>Revenue</TableHead><TableHead>Profit</TableHead><TableHead>Score</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(data.all_batches || []).map((b) => (
                    <TableRow key={b.batch_id}>
                      <TableCell className="font-medium">{b.batch_number}</TableCell>
                      <TableCell>{b.age_days}</TableCell>
                      <TableCell>{b.fcr ?? "—"}</TableCell>
                      <TableCell>{b.mortality_pct}</TableCell>
                      <TableCell>{fmt(b.total_revenue)}</TableCell>
                      <TableCell>{fmt(b.profit)}</TableCell>
                      <TableCell><Badge variant="outline">{b.performance_score}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
